import {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import Layout from "../../../core/layout/Layout.tsx";
import useService from "../../../hooks/useService.ts";
import LoadingComponent from "../../../components/LoadingComponent/LoadingComponent.tsx";
import {buscarViagensPorCte} from "../../../services/ModuloGerenciador/Viagens.ts";
import InputField from "../../../core/data-entry/InputField.tsx";
import Separator from "../../../core/components/Separator.tsx";
import './prestacaoDeServico.css';
import {debounce, DebouncedFunc} from "lodash";
import {useProfile} from "../../../contexts/ProfileContext.tsx";
import {ChangeEvent} from "react";
import SelectInputField from "../../../core/data-entry/SelectInputField.tsx";
import {useEmbarqueDependencias} from "../../../contexts/EmbarqueDependenciasContext.tsx";
import CustomButtonV2 from "../../../components/CustomButtonV2/CustomButtonV2";
import {cadastrarContratoPrestacaoServico} from "../../../services/ModuloGerenciador/PrestacaoDeServico.ts";
import {useNavigate} from "react-router-dom";
import {useToast} from "../../../contexts/ToastContext.tsx";
import {Embarque} from "../../../models/Embarque.ts";
import Divider from "../../../core/layout/Divider.tsx";
import {AnimatePresence, motion} from "framer-motion";
import ItemNotFound from "../../../components/ItemNotFound/ItemNotFound.tsx";

function CadastroPrestacaoDeServico() {
    const {profile} = useProfile();
    const {empresaSub} = useEmbarqueDependencias();

    const navigate = useNavigate();

    const {showToast} = useToast();

    const defaultEmpresaSubOptions = useMemo(() => {
        return empresaSub.map((empresa) => ({
            title: empresa.nome,
            obj: empresa.idEmpresaSub,
        }));
    }, [empresaSub]);

    const [viagemCte, setViagensCte] = useState<Embarque>(null);
    const [cte, setCte] = useState<string>("");
    const [valueSelect, setValueSelect] = useState<{ title: string; obj: string }>({title: "", obj: ""});
    const [empresaOptions, setEmpresaOptions] = useState<{ title: string; obj: string }[]>(defaultEmpresaSubOptions);
    const [validatorEmpresa, setValidatorEmpresa] = useState<boolean>(false);

    const [buscandoViagensCte, buscarViagensCte] = useService(buscarViagensPorCte, {
        autoStart: false,
        onData: (response) => {
            setViagensCte(response);
        },
        onError: () => {
            setViagensCte(null);
            showToast("CTE não encontrado.", "error");
        }
    });

    const [, cadastrarPrestacao] = useService(cadastrarContratoPrestacaoServico,
        {
            autoStart: false,
            onData: (response) => {
                showToast("Prestação de serviço cadastrada com sucesso.", "success");
                navigate(-1);
            },
            onError: (error) => {
                showToast(error.response?.data || "Erro ao cadastrar prestação de serviço.", "error");
            }
        }
    );

    const buscarRef = useRef(buscarViagensCte);
    const tokenRef = useRef<string | undefined>(profile?.token);

    const handleClearCte = useCallback(() => {
        setCte("");
    }, []);

    const debouncedBuscar = useMemo<DebouncedFunc<(value: string) => void>>(
        () =>
            debounce((value: string) => {
                buscarRef.current?.({
                    cte: value,
                    token: tokenRef.current || "",
                });
            }, 300),
        []
    );

    useEffect(() => {
        return () => {
            debouncedBuscar.cancel();
        };
    }, [debouncedBuscar]);

    useEffect(() => {
        buscarRef.current = buscarViagensCte;
    }, [buscarViagensCte]);

    useEffect(() => {
        tokenRef.current = profile?.token;
    }, [profile?.token]);

    const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target?.value ?? "";
        setCte(value);
        debouncedBuscar(value);
    }, [debouncedBuscar]);

    const handleInputEmpresaChange = useCallback((inputValue) => {
        const normalizedInput = inputValue.target.value.trim().toLowerCase();

        if (normalizedInput === "") {
            setEmpresaOptions(defaultEmpresaSubOptions);
            return;
        }

        setEmpresaOptions(empresaSub
            .filter((empresa) => {
                return empresa.nome.toLowerCase().includes(normalizedInput);
            })
            .sort((a, b) => a.nome.localeCompare(b.nome))
            .map((empresa) => ({
                title: empresa.nome,
                obj: empresa.idEmpresaSub,
            })));
    }, [empresaSub, defaultEmpresaSubOptions]);

    const handleCadastro = useCallback(() => {
        if (profile?.token) {
            if (!valueSelect.obj) {
                showToast("Selecione uma empresa subsidiária.", "error");
                setValidatorEmpresa(true);
                return;
            }
            if (!viagemCte) {
                showToast("Selecione uma viagem.", "error");
                return;
            }
            cadastrarPrestacao({
                token: profile.token,
                empresaSubId: valueSelect.obj,
                embarqueId: viagemCte.idEmbarque,
            });
        }
    }, [profile?.token, valueSelect, viagemCte, cadastrarPrestacao, showToast]);

    const handleClickCancelar = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const handleEmpresaChange = useCallback((selectedOption: { title: string; obj: string } | null) => {
        if (selectedOption) {
            setValueSelect(selectedOption);
            setValidatorEmpresa(false);
        }
    }, []);

    const handleClearEmpresa = useCallback(() => {
        setValueSelect(defaultEmpresaSubOptions);
    }, [defaultEmpresaSubOptions]);

    const isLoading = useMemo(() => {
        return buscandoViagensCte;
    }, [buscandoViagensCte]);

    return (
        <Layout title="Cadastro de Prestação de Serviço">
            <div className="content-container">
                <Separator size="xl"/>
                <div className="input-cte">
                    <SelectInputField
                        label="Empresa subsidiária"
                        placeholder="Selecione a empresa subsidiária"
                        value={valueSelect.title || ""}
                        options={empresaOptions}
                        onClear={handleClearEmpresa}
                        onChange={handleEmpresaChange}
                        onInputChange={handleInputEmpresaChange}
                        error={validatorEmpresa}
                    />
                </div>
                <Separator size="xl"/>
                <div className="input-cte">
                    <InputField
                        label="Digite o CTE"
                        onClear={handleClearCte}
                        onChange={handleInputChange}
                        value={cte}
                    />
                </div>

                <div className="list-viagem">
                    <AnimatePresence mode="wait">
                        {isLoading ? (
                            <LoadingComponent/>
                        ) : (
                            !!viagemCte ? (
                                <motion.div key="viagem-cte-detalhes"
                                            initial={{opacity: 0}}
                                            animate={{opacity: 1}}
                                            exit={{opacity: 0}}
                                            className="list-viagem"
                                >
                                    <div className="list-column">
                                        <div className="list-header">
                                            <p className="list-header-title">Unidade de Embarque:</p>
                                            <p>{viagemCte?.unidEmbarque ?? "Sem Unidade de Embarque"}</p>
                                            <Divider direction="horizontal"/>
                                        </div>
                                        <div className="list-header">
                                            <p className="list-header-title">Empresa:</p>
                                            <p>{viagemCte.empresaEmbarque?.nome ?? "Sem Empresa"}</p>
                                            <Divider direction="horizontal"/>
                                        </div>
                                        <div className="list-header">
                                            <p className="list-header-title">Filial:</p>
                                            <p>{viagemCte.filialEmbarque?.nome ?? "Sem filial"}</p>
                                            <Divider direction="horizontal"/>
                                        </div>
                                    </div>
                                    <div className="list-column">
                                        <div className="list-header">
                                            <p className="list-header-title">Pedido:</p>
                                            <p>{viagemCte.pedidoEmbarque?.pedido ?? "Sem Pedido"}</p>
                                            <Divider direction="horizontal"/>
                                        </div>
                                        <div className="list-header">
                                            <p className="list-header-title">Previsão de embarque:</p>
                                            <p>{new Date(viagemCte.previsaoEmbarque).toLocaleDateString("pt-BR", {
                                                month: "numeric",
                                                day: "numeric",
                                                hour: "numeric",
                                                minute: "numeric",
                                            }) ?? "Sem previsão de embarque"}</p>
                                            <Divider direction="horizontal"/>
                                        </div>
                                        <div className="list-header">
                                            <p className="list-header-title">Previsão de entrega:</p>
                                            <p>{new Date(viagemCte.previsaoEntrega).toLocaleDateString("pt-BR", {
                                                month: "numeric",
                                                day: "numeric",
                                                hour: "numeric",
                                                minute: "numeric",
                                            }) ?? "Sem previsão de entrega"}</p>
                                            <Divider direction="horizontal"/>
                                        </div>
                                    </div>
                                    <div className="list-column">
                                        <div className="list-header">
                                            <p className="list-header-title">Proprietário:</p>
                                            <p>{viagemCte.proprietario?.nome ?? "Sem Proprietário"}</p>
                                            <Divider direction="horizontal"/>
                                        </div>
                                        <div className="list-header">
                                            <p className="list-header-title">Motorista:</p>
                                            <p>{viagemCte.motoristaSelecionado?.nome ?? "Sem Motorista"}</p>
                                            <Divider direction="horizontal"/>
                                        </div>
                                        <div className="list-header">
                                            <p className="list-header-title">Destinatário:</p>
                                            <p>{viagemCte.destinatario ?? "Sem Destinatário"}</p>
                                            <Divider direction="horizontal"/>
                                        </div>
                                    </div>
                                </motion.div>
                            ) : (
                                <ItemNotFound label="Nenhum item foi localizado." />
                            )
                        )}
                    </AnimatePresence>
                </div>
                <div className="footer-container">
                    <CustomButtonV2
                        classCss="custom-button-cadastrar"
                        classText="button-texto-cadastrar"
                        icone="iconeSave"
                        titulo="Salvar"
                        onClick={handleCadastro}
                    />
                    <CustomButtonV2
                        classCss="custom-button-cancelar"
                        classText="button-texto-cancelar"
                        titulo="Cancelar"
                        cor="#0946B8"
                        onClick={handleClickCancelar}
                    />
                </div>
            </div>
        </Layout>
    )
};

export default memo(CadastroPrestacaoDeServico);